import React from 'react';
import { useParams } from 'react-router-dom';
import StatCard from '../components/StatCard';
import HealthGauge from '../components/HealthGauge';
import TrendBadge from '../components/TrendBadge';
import SeverityBadge from '../components/SeverityBadge';
import DataTable from '../components/DataTable';
import useAPI from '../hooks/useAPI';
import { getRobotJournal } from '../services/api';

export default function RobotJournal() {
  const { projectId, robotNumber } = useParams();
  const { data, loading, error } = useAPI(
    () => getRobotJournal(projectId, robotNumber),
    [projectId, robotNumber]
  );

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading robot journal...</div>;
  if (error) return <div style={{ padding: 40, color: '#d63031' }}>Error: {error}</div>;
  if (!data) return null;

  return (
    <div>
      <div style={styles.header}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>
            Robot #{data.robot_number}
          </h1>
          <div style={{ fontSize: 13, color: '#636e72', marginTop: 4 }}>
            {data.project} | SW {data.sw_version} | HW {data.hw_version} | Status: {data.status}
          </div>
        </div>
        <HealthGauge score={data.health_score} label={data.health_label?.toUpperCase()} size={90} />
      </div>

      {/* Attention Items */}
      {data.attention_items?.length > 0 && (
        <div style={styles.alertBox}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Attention Required</div>
          {data.attention_items.map((item, i) => (
            <div key={i} style={styles.alertItem}>{item}</div>
          ))}
        </div>
      )}

      {/* Stats Row */}
      <div style={styles.cardGrid}>
        <StatCard label="Errors (90d)" value={data.errors_90d} color="#d63031" />
        <StatCard label="7d Trend" value={<TrendBadge trend={data.trend_7d?.trend} />} color="#636e72" />
        <StatCard label="Avg/Day (7d)" value={data.trend_7d?.avg_per_day} color="#e17055" />
        <StatCard label="Critical (7d)" value={data.trend_7d?.critical} color="#c0392b" />
        <StatCard label="Last Error" value={data.last_error ? new Date(data.last_error).toLocaleDateString() : 'None'} color="#0984e3" />
      </div>

      <div style={styles.twoCol}>
        {/* Top Errors */}
        <div>
          <h2 style={styles.sectionTitle}>Top Errors</h2>
          <DataTable
            columns={[
              { key: 'code', label: 'Error Code' },
              { key: 'count', label: 'Count', align: 'right' },
            ]}
            data={(data.top_errors || []).slice(0, 10)}
          />

          {/* Chronic Errors */}
          {data.chronic_errors?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h2 style={styles.sectionTitle}>Chronic Errors</h2>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {data.chronic_errors.map(code => (
                  <span key={code} style={styles.chronicBadge}>{code}</span>
                ))}
              </div>
            </div>
          )}

          {/* Location Hotspots */}
          {data.location_hotspots?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h2 style={styles.sectionTitle}>Location Hotspots</h2>
              <div style={{ fontSize: 13, color: '#636e72' }}>
                Frequently fails at rows: {data.location_hotspots.join(', ')}
              </div>
            </div>
          )}
        </div>

        {/* Repairs */}
        <div>
          <h2 style={styles.sectionTitle}>Repair History</h2>
          {(data.repairs || []).length > 0 ? (
            <DataTable
              columns={[
                { key: 'date', label: 'Date', render: v => new Date(v).toLocaleDateString() },
                { key: 'error_code', label: 'Error' },
                { key: 'parts', label: 'Parts', render: v => v?.join(', ') || '-' },
                { key: 'technician', label: 'Tech' },
                {
                  key: 'effective',
                  label: 'Effective',
                  render: v => (
                    <span style={{ color: v ? '#00b894' : '#d63031', fontWeight: 600 }}>
                      {v ? 'Yes' : 'No'}
                    </span>
                  ),
                },
              ]}
              data={data.repairs}
            />
          ) : (
            <div style={{ fontSize: 13, color: '#999', padding: 20, textAlign: 'center', background: '#fff', borderRadius: 10 }}>
              No repair history available
            </div>
          )}

          {/* Recommendations */}
          {data.recommendations?.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <h2 style={styles.sectionTitle}>Recommendations</h2>
              {data.recommendations.map((rec, i) => (
                <div key={i} style={styles.recItem}>
                  <span style={styles.recPriority}>P{rec.priority}</span>
                  <span>{rec.description}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Time Patterns */}
      {data.time_patterns && Object.keys(data.time_patterns).length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={styles.sectionTitle}>Error Timing Pattern</h2>
          <div style={styles.timeChart}>
            {Object.entries(data.time_patterns).map(([hour, count]) => (
              <div key={hour} style={styles.timeBar}>
                <div
                  style={{
                    ...styles.timeBarFill,
                    height: `${Math.min(count * 5, 100)}%`,
                  }}
                />
                <div style={styles.timeLabel}>{hour.split(':')[0]}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  alertBox: {
    background: '#fff3cd',
    borderRadius: 8,
    padding: '14px 18px',
    marginBottom: 20,
    borderLeft: '4px solid #d68910',
  },
  alertItem: {
    fontSize: 13,
    padding: '3px 0',
    color: '#856404',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: 14,
    marginBottom: 24,
  },
  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  chronicBadge: {
    padding: '4px 10px',
    borderRadius: 4,
    background: '#ffe0e0',
    color: '#c0392b',
    fontSize: 12,
    fontWeight: 500,
  },
  recItem: {
    display: 'flex',
    gap: 10,
    padding: '10px 14px',
    background: '#fff',
    borderRadius: 8,
    marginBottom: 8,
    fontSize: 13,
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    alignItems: 'flex-start',
  },
  recPriority: {
    background: '#6c5ce7',
    color: '#fff',
    borderRadius: 4,
    padding: '2px 8px',
    fontWeight: 700,
    fontSize: 11,
    flexShrink: 0,
  },
  timeChart: {
    display: 'flex',
    gap: 2,
    height: 80,
    background: '#fff',
    borderRadius: 10,
    padding: '12px 16px',
    alignItems: 'flex-end',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  timeBar: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-end',
    height: '100%',
  },
  timeBarFill: {
    width: '70%',
    background: '#6c5ce7',
    borderRadius: '3px 3px 0 0',
    minHeight: 2,
    transition: 'height 0.3s',
  },
  timeLabel: {
    fontSize: 9,
    color: '#999',
    marginTop: 4,
  },
};
